import type { RequisicaoFalhou } from '@/services/api'

export interface ErrosFormulario {
  porCampo: Record<string, string>
  geral: string | null
}

/**
 * Traduz a falha do interceptor em erros por campo (`VALIDATION_ERROR` → `data{campo}`) ou
 * numa mensagem geral. Os nomes dos campos são os do body Java, que os formulários usam
 * como chave — assim o destaque cai no campo certo sem mapa intermediário.
 */
export const errosDaFalha = (e: unknown): ErrosFormulario => {
  const falha = e as RequisicaoFalhou | undefined
  const corpo = falha?.data as
    | { errorCode?: string; data?: Record<string, string>; message?: string }
    | undefined
  if (corpo?.errorCode === 'VALIDATION_ERROR' && corpo.data && typeof corpo.data === 'object') {
    return { porCampo: { ...corpo.data }, geral: null }
  }
  const p1 = corpo?.errorCode ? MENSAGENS_ERRO_POLITICA_P1[corpo.errorCode] : undefined
  if (p1) return { porCampo: {}, geral: p1 }
  return { porCampo: {}, geral: falha?.message ?? 'Não foi possível salvar.' }
}

/**
 * P1 (AN-PP1 §2.4): recusas 422 do cadastro de política por código/status do catálogo
 * (backend DEV-PP1-S em paralelo — códigos do desenho; ajustar na integração).
 */
export const MENSAGENS_ERRO_POLITICA_P1: Record<string, string> = {
  PRODUTO_SEM_POLITICA_SIMULACAO:
    'Este produto está como "Simulação indisponível" no catálogo: não aceita política com fórmula. ' +
    'Para dar uma fórmula a ele, o ADMIN altera antes o status do produto no catálogo.',
  MODELO_INCOMPATIVEL_COM_PRODUTO:
    'Modelo incompatível com o produto: o modelo Campanha só vale para a Campanha de Incentivo, ' +
    'e a Campanha de Incentivo só aceita o modelo Campanha.',
  PRODUTO_SEM_SIMULACAO:
    '"Outra necessidade" não tem simulação: não aceita política de produto, spread nem elegibilidade.',
  PRODUTO_CODIGO_DIVERGENTE:
    'O código do produto enviado não confere com o catálogo. Recarregue a página e tente de novo.',
}

/**
 * P4 (AN-PP4 §2.5): o cadastro da política de comissão recusa com 422 `SimulacaoRegraException`
 * (`errorCode` + `message` + `data` com o erro POR CAMPO — contrato real do B1):
 * `FAIXAS_INVALIDAS` → `data{ "faixas[i].valorAte" | "faixas[i].percentual" | "faixas[i].ateInclusivo" |
 * "faixas[i].ordem" | "faixas[i].rotulo" | "faixas[i]" | "faixas": msg }`; `VIGENCIA_INVALIDA` →
 * `data{ vigenciaInicio: msg }`. Os campos que o diálogo exibe ao lado (valorAte, percentual, 360,
 * vigência, origem) ficam com a própria chave; os de faixa sem campo próprio na tela (inclusive,
 * ordem, rótulo, faixa vazia, lista vazia) são reunidos no destaque `faixas`, com o número da faixa.
 * Sem `data` (backend antigo), o código decide o destaque e o texto é o `message` do backend.
 */
export const CAMPO_DO_ERRO_COMISSAO: Record<string, string> = {
  FAIXAS_INVALIDAS: 'faixas',
  VIGENCIA_INVALIDA: 'vigenciaInicio',
}

/** Chaves de `data` que o diálogo destaca no próprio campo. */
const CAMPO_EXIBIDO_COMISSAO = /^(faixas\[\d+\]\.(valorAte|percentual)|percentualReceitaLiquida360|vigenciaInicio|origem)$/

const rotuloChaveFaixa = (chave: string): string => {
  const m = /^faixas\[(\d+)\]/.exec(chave)
  return m ? `Faixa ${Number(m[1]) + 1}: ` : ''
}

export const errosDaFalhaComissao = (e: unknown): ErrosFormulario => {
  const falha = e as RequisicaoFalhou | undefined
  const corpo = falha?.data as
    | { errorCode?: string; message?: string; data?: Record<string, string> | null }
    | undefined
  const campo = corpo?.errorCode ? CAMPO_DO_ERRO_COMISSAO[corpo.errorCode] : undefined
  if (!campo) return errosDaFalha(e)
  const porCampoBackend =
    corpo?.data && typeof corpo.data === 'object' ? Object.entries(corpo.data) : []
  if (porCampoBackend.length === 0) {
    return { porCampo: { [campo]: corpo?.message ?? falha?.message ?? 'Valor recusado' }, geral: null }
  }
  const porCampo: Record<string, string> = {}
  const deFaixa: string[] = []
  for (const [chave, msg] of porCampoBackend) {
    if (CAMPO_EXIBIDO_COMISSAO.test(chave)) porCampo[chave] = msg
    else if (chave.startsWith('faixas')) deFaixa.push(rotuloChaveFaixa(chave) + msg)
    else deFaixa.push(`${chave}: ${msg}`)
  }
  if (deFaixa.length) porCampo.faixas = deFaixa.join(' · ')
  return { porCampo, geral: null }
}

export const HELPER_ORIGEM ='E-mail, ata ou contrato que aprovou esta versão'
